/**
 * Signal handling utilities for graceful shutdown
 */

import chalk from 'chalk';

export interface SignalHandlerOptions {
  /** Cleanup function to call when signal is received */
  cleanup: (signal?: string) => Promise<void>;
  /** Whether to log cleanup messages (default: true) */
  logCleanup?: boolean;
}

/**
 * Create a signal handler that calls cleanup and exits with appropriate code.
 * Includes a guard to prevent re-entrant cleanup if a second signal arrives
 * while the first cleanup is still running.
 */
export function createSignalHandler(
  cleanup: SignalHandlerOptions['cleanup'],
  logCleanup = true
): (signal: string) => void {
  let isCleaningUp = false;

  return (signal: string) => {
    if (isCleaningUp) {
      if (logCleanup) {
        console.log(chalk.dim('\nCleanup already in progress, please wait...'));
      }
      return;
    }
    isCleaningUp = true;

    if (logCleanup) {
      console.log(chalk.dim(`\n\nReceived ${signal}, cleaning up...`));
    }
    cleanup(signal)
      .then(() => {
        process.exit(signal === 'SIGINT' ? 130 : 143);
      })
      .catch((err) => {
        console.error(chalk.red(`Cleanup error: ${err}`));
        process.exit(1);
      });
  };
}

/** Tracks registered handlers so we can remove only ours (not all listeners). */
const registeredHandlers = new Map<NodeJS.Signals, (signal: string) => void>();

/**
 * Register signal handlers for graceful shutdown
 *
 * @param signals - Signals to handle (default: SIGINT, SIGTERM, SIGHUP)
 * @param handler - Handler function to call on signal
 */
export function registerSignalHandlers(
  handler: (signal: string) => void,
  signals: NodeJS.Signals[] = ['SIGINT', 'SIGTERM', 'SIGHUP']
): void {
  for (const signal of signals) {
    registeredHandlers.set(signal, handler);
    process.on(signal, handler);
  }
}

/**
 * Remove only the signal handlers we registered (not all listeners).
 *
 * @param signals - Signals to remove handlers for (default: SIGINT, SIGTERM, SIGHUP)
 */
export function removeSignalHandlers(
  signals: NodeJS.Signals[] = ['SIGINT', 'SIGTERM', 'SIGHUP']
): void {
  for (const signal of signals) {
    const handler = registeredHandlers.get(signal);
    if (handler) {
      process.removeListener(signal, handler);
      registeredHandlers.delete(signal);
    }
  }
}
